import type { JSX } from 'react';
import type { AdminCustomerPackage, AdminPackageServiceKind } from '../../lib/api.js';
import './PackageBalance.css';

/**
 * Becky-T1.3 (issue #26) — package balance + remaining-days countdown.
 *
 * The package balance is now server-of-record (issue #140): the store
 * helpers that used to live here moved to `adminPaymentsApi`
 * (`listPackages` / `sellPackage` / `redeemPackage` in `lib/api.ts`).
 * This file keeps only the presentational component + pure helpers; the
 * `CustomerPackage` shape is the server type `AdminCustomerPackage`.
 */

// ─── Domain types ─────────────────────────────────────────────────────

export type PackageServiceKind = AdminPackageServiceKind;

const PACKAGE_KIND_LABELS: Record<PackageServiceKind, string> = {
  daycare: 'Daycare',
  daycare_half: 'Half-day daycare',
  boarding: 'Boarding',
  grooming: 'Grooming',
};

/**
 * #1157 — a package kind as the desk reads it. 'daycare' stays "Daycare"
 * (a full day, the default everyone means); the half-day kind says so.
 * `serviceKind` is server TEXT, so an unknown value reads back as itself.
 */
export function packageKindLabel(kind: string): string {
  return (PACKAGE_KIND_LABELS as Record<string, string>)[kind] ?? kind;
}

/** "10-day Half-day daycare package" — the label when the sale named none. */
export function defaultPackageLabel(pkg: Pick<CustomerPackage, 'serviceKind' | 'totalDays'>): string {
  return `${pkg.totalDays}-day ${packageKindLabel(pkg.serviceKind)} package`;
}

/**
 * The package shape the UI renders. Aliased to the server type so callers
 * that import `CustomerPackage` keep working after the server migration.
 */
export type CustomerPackage = AdminCustomerPackage;

const COURTESY_REMINDER_THRESHOLD = 1;

// ─── Helpers ──────────────────────────────────────────────────────────

const defaultLabel = defaultPackageLabel;

/** `YYYY-MM-DD` → "Jan 31, 2027" (a calendar day — no timezone shift). */
function formatCalendarDay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Today's calendar date on this device, `YYYY-MM-DD`. */
function localTodayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * #1157 E2.6 — past its last day. The server decides with the FACILITY's
 * date and refuses an expired package anyway; this is the screen's reading.
 */
export function isExpired(expiresOn: string | null | undefined, todayIso: string = localTodayIso()): boolean {
  return Boolean(expiresOn) && (expiresOn as string).slice(0, 10) < todayIso;
}

export function shouldOfferCourtesyReminder(pkg: CustomerPackage): boolean {
  return pkg.remainingDays > 0 && pkg.remainingDays <= COURTESY_REMINDER_THRESHOLD;
}

// ─── UI variants ──────────────────────────────────────────────────────

type Variant = 'pet-card' | 'checkout-preview' | 'post-checkout';

interface PackageBalanceProps {
  readonly pkg: CustomerPackage | null;
  readonly variant: Variant;
  /** Days to decrement at checkout (defaults to 1). */
  readonly daysToDeduct?: number;
  /** Called when staff click "Send courtesy reminder" on the post-checkout summary. */
  readonly onSendCourtesyReminder?: () => void;
  /**
   * #1157 E2.2 — with `pkg: null`, WHY there is nothing to show. Absent =
   * the read succeeded and there is no package ("No package on file").
   * `loading` = still asking. `error` = the read FAILED: the balance is
   * unknown, which the desk must never read as zero.
   */
  readonly emptyReason?:
    | { readonly kind: 'loading' }
    | { readonly kind: 'error'; readonly message: string; readonly onRetry?: () => void };
}

/**
 * Renders the package balance UI. Three variants:
 *
 * - `pet-card` — compact summary surfaced on a customer's pet card or
 *   the customer overview ("3 days remaining of 10-day Daycare package").
 *   Used by `CustomerDetail.tsx`.
 * - `checkout-preview` — "3 days → 2 days after this" preview shown
 *   at checkout when 'Apply Package' is the chosen payment method.
 *   Used by `Bookings.tsx`.
 * - `post-checkout` — confirmation rendered after the booking checks
 *   out, including the courtesy-reminder CTA when the balance drops
 *   to ≤1 day. Used by `Bookings.tsx`.
 */
export function PackageBalance({
  pkg,
  variant,
  daysToDeduct = 1,
  onSendCourtesyReminder,
  emptyReason,
}: PackageBalanceProps): JSX.Element {
  if (!pkg && emptyReason?.kind === 'loading') {
    return (
      <div
        className="ferro-package-balance ferro-package-balance--empty"
        role="status"
        data-testid="package-balance-loading"
      >
        <span className="ferro-package-balance__label">Package balance</span>
        <span className="ferro-package-balance__value-muted">Loading…</span>
      </div>
    );
  }
  if (!pkg && emptyReason?.kind === 'error') {
    return (
      <div
        className="ferro-package-balance ferro-package-balance--empty ferro-package-balance--error"
        role="alert"
        data-testid="package-balance-error"
      >
        <span className="ferro-package-balance__label">Package balance</span>
        <span className="ferro-package-balance__value-error">{emptyReason.message}</span>
        {emptyReason.onRetry ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-package-balance__retry"
            onClick={emptyReason.onRetry}
            data-testid="package-balance-retry"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }
  if (!pkg) {
    return (
      <div
        className="ferro-package-balance ferro-package-balance--empty"
        data-testid="package-balance-empty"
      >
        <span className="ferro-package-balance__label">Package balance</span>
        <span className="ferro-package-balance__value-muted">
          No package on file
        </span>
      </div>
    );
  }

  const label = pkg.label ?? defaultLabel(pkg);
  const remaining = pkg.remainingDays;
  const post = Math.max(0, remaining - daysToDeduct);
  const shouldRemind =
    variant === 'post-checkout' &&
    shouldOfferCourtesyReminder({ ...pkg, remainingDays: post });

  if (variant === 'pet-card') {
    return (
      <div
        className="ferro-package-balance ferro-package-balance--pet-card"
        data-testid="package-balance-pet-card"
      >
        <div className="ferro-package-balance__label">{label}</div>
        <div className="ferro-package-balance__big">
          <strong data-testid="package-balance-remaining">{remaining}</strong>
          <span className="ferro-package-balance__big-unit">
            day{remaining === 1 ? '' : 's'} remaining
          </span>
        </div>
        <div className="ferro-package-balance__sub">
          of {pkg.totalDays}-day package
        </div>
        {/* #1157 E2.6 — the expiry, in words, never colour alone. */}
        {pkg.expiresOn ? (
          <div
            className={`ferro-package-balance__expiry${isExpired(pkg.expiresOn) ? ' ferro-package-balance__expiry--past' : ''}`}
            data-testid="package-balance-expiry"
          >
            {isExpired(pkg.expiresOn)
              ? `Expired ${formatCalendarDay(pkg.expiresOn)} — extend it to use it`
              : `Expires ${formatCalendarDay(pkg.expiresOn)}`}
          </div>
        ) : null}
      </div>
    );
  }

  if (variant === 'checkout-preview') {
    return (
      <div
        className="ferro-package-balance ferro-package-balance--preview"
        data-testid="package-balance-preview"
      >
        <div className="ferro-package-balance__label">{label}</div>
        <div className="ferro-package-balance__transition">
          <span data-testid="package-balance-pre">
            {remaining} day{remaining === 1 ? '' : 's'}
          </span>
          <span
            className="ferro-package-balance__arrow"
            aria-hidden="true"
          >
            →
          </span>
          <span data-testid="package-balance-post">
            {post} day{post === 1 ? '' : 's'} after this
          </span>
        </div>
      </div>
    );
  }

  // post-checkout
  return (
    <div
      className="ferro-package-balance ferro-package-balance--post"
      data-testid="package-balance-post-checkout"
    >
      <div className="ferro-package-balance__label">{label}</div>
      <div className="ferro-package-balance__transition">
        <span>
          {remaining} day{remaining === 1 ? '' : 's'}
        </span>
        <span className="ferro-package-balance__arrow" aria-hidden="true">
          →
        </span>
        <span data-testid="package-balance-post-final">
          <strong>
            {post} day{post === 1 ? '' : 's'}
          </strong>{' '}
          remaining
        </span>
      </div>
      {shouldRemind ? (
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-package-balance__reminder"
          onClick={onSendCourtesyReminder}
          data-testid="package-balance-courtesy-reminder"
        >
          Send courtesy reminder
        </button>
      ) : null}
    </div>
  );
}
